import React from "react";
import { motion } from "framer-motion";
import { useColorMode } from "@chakra-ui/react";
import { Link } from "react-router-dom";

const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,800&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&family=IBM+Plex+Mono:wght@400;500&display=swap');`;

/* ─── CONTENT ──────────────────────────────────────────────────────────────
   Sourced from the existing Footer.jsx Privacy Policy modal, restructured
   for a full page. Edit copy here — layout below reads from this array. */
const SECTIONS = [
  {
    n: "01",
    title: "Information We Collect",
    intro: "We collect the following types of information:",
    bullets: [
      "Personal identification information (name, email address, phone number)",
      "Educational and professional background",
      "Payment and billing information",
      "Usage data and platform interactions",
      "Device and browser information",
    ],
  },
  {
    n: "02",
    title: "How We Use Your Information",
    body:
      "Your information is used to provide and maintain our services, process transactions, send administrative information, improve our platform, communicate with you, and comply with legal obligations.",
  },
  {
    n: "03",
    title: "Information Sharing",
    body:
      "We do not sell your personal information. We may share data with trusted third-party service providers who assist in operating our platform, processing payments, or delivering services, subject to confidentiality obligations.",
  },
  {
    n: "04",
    title: "Data Security",
    body:
      "We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.",
  },
  {
    n: "05",
    title: "Your Rights",
    intro: "You can, at any time, ask us to:",
    bullets: [
      "Access your personal data",
      "Request correction of inaccurate data",
      "Request deletion of your data",
      "Object to processing of your data",
      "Request data portability",
      "Withdraw consent at any time",
    ],
  },
  {
    n: "06",
    title: "Cookies & Tracking",
    body:
      "We use cookies and similar tracking technologies to track activity on our platform and hold certain information to improve user experience.",
  },
  {
    n: "07",
    title: "Changes to This Policy",
    body:
      "We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.",
  },
];

/* ─── ICON ─────────────────────────────────────────────────────────────────── */
const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const ArrowLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="17" y1="12" x2="7" y2="12" /><polyline points="11 6 5 12 11 18" />
  </svg>
);

/* ─── SECTION CARD ─────────────────────────────────────────────────────────── */
function SectionCard({ data, isDark }) {
  const cardBg    = isDark ? "#16161f"                : "#ffffff";
  const cardBorder= isDark ? "rgba(255,255,255,0.09)" : "#E5E7EB";
  const textPri   = isDark ? "rgba(255,255,255,0.92)" : "#111827";
  const textMuted = isDark ? "rgba(255,255,255,0.55)" : "#4B5563";
  const numColor  = isDark ? "rgba(2,142,196,0.55)"   : "rgba(2,142,196,0.45)";
  const shadow    = isDark ? "0 4px 18px rgba(0,0,0,0.3)" : "0 2px 10px rgba(0,0,0,0.05)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.4 }}
      style={{
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: 18,
        padding: "28px 28px 26px",
        display: "flex",
        gap: 20,
        boxShadow: shadow,
      }}
    >
      <span style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 13, fontWeight: 500, color: numColor,
        flexShrink: 0, paddingTop: 2, letterSpacing: "0.02em",
      }}>
        {data.n}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontSize: 18, fontWeight: 800, color: textPri, margin: "0 0 10px",
        }}>
          {data.title}
        </h3>
        {data.body && (
          <p style={{ fontSize: 14, color: textMuted, lineHeight: 1.75, margin: 0 }}>
            {data.body}
          </p>
        )}
        {data.intro && (
          <p style={{ fontSize: 14, color: textMuted, lineHeight: 1.75, margin: "0 0 10px" }}>
            {data.intro}
          </p>
        )}
        {data.bullets && (
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
            {data.bullets.map((b) => (
              <li key={b} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14, color: textMuted, lineHeight: 1.65 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#028EC4", flexShrink: 0, marginTop: 8 }} />
                {b}
              </li>
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  );
}

/* ─── MAIN PAGE ────────────────────────────────────────────────────────────── */
export default function PrivacyPolicy() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  const pageBg     = isDark ? "#09090f"                : "#F8F9FB";
  const heroBorder = isDark ? "rgba(255,255,255,0.07)" : "#E5E7EB";
  const heroGlow   = isDark ? "rgba(2,142,196,0.11)"   : "rgba(2,142,196,0.06)";
  const textPri    = isDark ? "rgba(255,255,255,0.93)" : "#111827";
  const textMuted  = isDark ? "rgba(255,255,255,0.48)" : "#6B7280";
  const pillBg     = isDark ? "rgba(2,142,196,0.12)"   : "rgba(2,142,196,0.08)";
  const pillBorder = isDark ? "rgba(2,142,196,0.35)"   : "rgba(2,142,196,0.22)";
  const footBg     = isDark ? "rgba(255,255,255,0.03)" : "rgba(2,142,196,0.03)";
  const footBorder = isDark ? "rgba(255,255,255,0.07)" : "rgba(2,142,196,0.12)";

  return (
    <>
      <style>{`
        ${FONT_LINK}
        * { box-sizing: border-box; }
        .pp-page { font-family: 'DM Sans', sans-serif; }
        @media (max-width: 768px) {
          .pp-hero { padding: 52px 20px 40px !important; }
          .pp-inner { padding: 0 16px !important; }
        }
      `}</style>

      <div className="pp-page" style={{ background: pageBg, minHeight: "100vh", padding: "0 0 100px", color: textPri }}>

        {/* ── HERO ── */}
        <div className="pp-hero" style={{
          textAlign: "center", padding: "80px 24px 56px",
          background: `radial-gradient(ellipse 80% 45% at 50% 0%, ${heroGlow} 0%, transparent 70%)`,
          borderBottom: `1px solid ${heroBorder}`,
        }}>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link to="/" style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              color: textMuted, fontSize: 12.5, fontWeight: 600,
              textDecoration: "none", marginBottom: 22,
            }}>
              <ArrowLeftIcon /> Back to Home
            </Link>

            <div>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                background: pillBg, border: `1px solid ${pillBorder}`,
                color: "#028EC4", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em",
                textTransform: "uppercase", padding: "6px 16px", borderRadius: 999, marginBottom: 22,
              }}>
                <ShieldIcon />
                Your Data, Handled Responsibly
              </span>
            </div>

            <h1 style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: "clamp(2.4rem, 6vw, 4rem)", fontWeight: 800,
              lineHeight: 1.08, margin: "0 0 16px", color: textPri,
            }}>
              Privacy{" "}
              <span style={{ background: "linear-gradient(135deg, #028EC4, #38BDF8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Policy
              </span>
            </h1>

            <p style={{
              fontSize: "clamp(14px, 2vw, 16px)", color: textMuted,
              maxWidth: 560, margin: "0 auto", lineHeight: 1.8,
            }}>
              How GrapplTech collects, uses, and protects your information across our bootcamps, workshops, and platform.
            </p>
            <p style={{ fontSize: 12.5, color: textMuted, marginTop: 14, fontFamily: "'IBM Plex Mono', monospace" }}>
              Last updated: {new Date().getFullYear()}
            </p>
          </motion.div>
        </div>

        <div className="pp-inner" style={{ maxWidth: 820, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ marginTop: 56, display: "flex", flexDirection: "column", gap: 16 }}>
            {SECTIONS.map((s) => (
              <SectionCard key={s.n} data={s} isDark={isDark} />
            ))}
          </div>

          {/* ── FOOTER NOTE ── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.45 }}
            style={{
              marginTop: 40, textAlign: "center",
              background: footBg, border: `1px solid ${footBorder}`,
              borderRadius: 16, padding: "26px 28px",
            }}
          >
            <p style={{ fontSize: 13.5, color: textMuted, margin: 0, lineHeight: 1.7 }}>
              For privacy-related inquiries, contact us at{" "}
              <a href="mailto:support@grappl-tech.in" style={{ color: "#028EC4", fontWeight: 600, textDecoration: "none" }}>
                support@grappl-tech.in
              </a>
            </p>
          </motion.div>
        </div>
      </div>
    </>
  );
}